import { CatalogEntry, DesignVersion, type ApiClient } from "@cfg/api-contract";
import type { BrandConfig } from "@cfg/brand-sdk";
import { compareSemver } from "@cfg/design-sdk";
import { z } from "zod";
import { displayTitle, sortByTitleOrder } from "./titles.ts";

/** Where designs come from. The shell only ever sees URLs to immutable bundles. */
export interface RegistryClient {
  /** Latest version of every design the brand shows, ordered by the authors' "NN." title prefix, titles shown without it. */
  catalog(): Promise<CatalogEntry[]>;
  versions(designId: string): Promise<{ versions: DesignVersion[]; channels: Record<string, string> }>;
  /** Resolve a specific version's bundle URL (falls back to the catalog entry). */
  resolve(designId: string, version?: string): Promise<{ version: string; bundleUrl: string; latest: string; deprecated: boolean } | null>;
}

const LocalRegistryFile = z.object({
  designs: z.array(
    z.object({
      id: z.string(),
      version: z.string(),
      pack: z.string(),
      title: z.string(),
      description: z.string().optional(),
      tags: z.array(z.string()).default([]),
      kernel: z.enum(["manifold", "replicad"]).default("manifold"),
      complexity: z.enum(["low", "medium", "high"]).optional(),
      hash: z.string(),
      bundleUrl: z.string(),
      thumbnailUrl: z.string().optional(),
      jsonSchema: z.record(z.string(), z.unknown()).optional(),
      publishedAt: z.string().optional(),
      brands: z.array(z.string()).default([]),
      channel: z.string().optional(),
      family: z.string().optional(),
      variant: z.string().optional(),
    }),
  ),
});

function globMatch(pattern: string, id: string): boolean {
  if (pattern === "*") return true;
  if (pattern.endsWith(".*")) return id.startsWith(pattern.slice(0, -1)) || id === pattern.slice(0, -2);
  return pattern === id;
}

/** Brand catalog filter (also applied server-side; here for the local registry). */
export function visibleToBrand(entry: { id: string; tags: string[]; pack: string }, brand: BrandConfig): boolean {
  const packIds = brand.packs.map((p) => p.split("@")[0]);
  if (!packIds.includes(entry.pack)) return false;
  const { include, exclude, tags } = brand.catalog;
  if (include.length && !include.some((p) => globMatch(p, entry.id))) return false;
  if (exclude.some((p) => globMatch(p, entry.id))) return false;
  if (tags.length && !entry.tags.some((t) => tags.includes(t))) return false;
  return true;
}

/** Registry built from `design build` output served at /packs (dev) or any static host. */
export class LocalRegistry implements RegistryClient {
  private cache?: Promise<z.infer<typeof LocalRegistryFile>["designs"]>;
  constructor(
    private readonly url: string,
    private readonly brand: BrandConfig,
  ) {}

  private load() {
    this.cache ??= fetch(this.url, { cache: "no-store" })
      .then(async (r) => {
        if (!r.ok) throw new Error(`Registry ${this.url} returned ${r.status}`);
        return LocalRegistryFile.parse(await r.json()).designs;
      })
      .catch((err) => {
        this.cache = undefined;
        throw err;
      });
    return this.cache;
  }

  async catalog(): Promise<CatalogEntry[]> {
    const all = (await this.load()).filter((d) => visibleToBrand(d, this.brand));
    const latest = new Map<string, (typeof all)[number]>();
    for (const d of all) {
      const cur = latest.get(d.id);
      if (!cur || compareSemver(d.version, cur.version) > 0) latest.set(d.id, d);
    }
    return sortByTitleOrder([...latest.values()]).map((d) =>
      CatalogEntry.parse({
        id: d.id,
        pack: d.pack,
        title: displayTitle(d.title),
        description: d.description,
        tags: d.tags,
        thumbnailUrl: d.thumbnailUrl,
        channel: "latest",
        version: d.version,
        bundleUrl: d.bundleUrl,
        hash: d.hash,
        kernel: d.kernel,
        complexity: d.complexity,
        family: d.family,
        variant: d.variant,
      }),
    );
  }

  async versions(designId: string) {
    const list = (await this.load()).filter((d) => d.id === designId).sort((a, b) => compareSemver(a.version, b.version));
    const versions = list.map((d) =>
      DesignVersion.parse({
        id: d.id,
        version: d.version,
        pack: d.pack,
        bundleUrl: d.bundleUrl,
        hash: d.hash,
        kernel: d.kernel,
        jsonSchema: d.jsonSchema ?? {},
        publishedAt: d.publishedAt ?? new Date(0).toISOString(),
        thumbnailUrl: d.thumbnailUrl,
      }),
    );
    const latest = versions.at(-1)?.version;
    const channels: Record<string, string> = {};
    if (latest) channels.latest = latest;
    return { versions, channels };
  }

  async resolve(designId: string, version?: string) {
    const { versions, channels } = await this.versions(designId);
    if (!versions.length) return null;
    const latest = channels.latest ?? versions.at(-1)!.version;
    const v = version ? versions.find((x) => x.version === version) : versions.find((x) => x.version === latest);
    if (!v) return null;
    return { version: v.version, bundleUrl: v.bundleUrl, latest, deprecated: v.deprecated };
  }
}

/**
 * Registry backed by the shared API. The API scopes by pack, which is what keeps
 * one brand's designs out of another's catalog; the `catalog` include/exclude/tag
 * rules live only in the brand config, so they are applied here.
 */
export class ApiRegistry implements RegistryClient {
  constructor(
    private readonly api: ApiClient,
    private readonly brand: BrandConfig,
  ) {}
  async catalog() {
    const designs = (await this.api.catalog()).designs.filter((d) => visibleToBrand({ ...d, tags: d.tags ?? [] }, this.brand));
    return sortByTitleOrder(designs).map((d) => ({ ...d, title: displayTitle(d.title) }));
  }
  async versions(designId: string) {
    return this.api.designVersions(designId);
  }
  async resolve(designId: string, version?: string) {
    const { versions, channels } = await this.versions(designId);
    if (!versions.length) return null;
    const latest = channels.latest ?? versions.at(-1)!.version;
    const v = version ? versions.find((x) => x.version === version) : versions.find((x) => x.version === latest);
    if (!v) return null;
    return { version: v.version, bundleUrl: v.bundleUrl, latest, deprecated: v.deprecated };
  }
}
